<template>
  <v-slide-x-transition>
    <v-sheet
      v-if="isOpen && groupId"
      v-click-outside="clickOutsideConfig"
      class="primary-palette elevation-4"
      color="bg2"
      :id="`palette-${groupId}`"
      role="menu"
      :aria-label="`${groupTitle} menu`"
    >
      <!-- Header -->
      <div class="palette-header">
        <slot name="header" :title="groupTitle">
          <h3 class="palette-title">{{ groupTitle }}</h3>
          <v-spacer />
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                :color="isPinned ? 'primary' : 'grey'"
                @click="handlePinToggle"
                :aria-pressed="isPinned"
                aria-label="Pin menu open"
              >
                <v-icon :size="18">{{ isPinned ? 'mdi-pin' : 'mdi-pin-outline' }}</v-icon>
              </v-btn>
            </template>
            <span>{{ isPinned ? 'Unpin menu' : 'Pin menu open' }}</span>
          </v-tooltip>
        </slot>
      </div>

      <!-- Menu Items -->
      <div class="palette-body" ref="menuList">
        <v-list dense class="pa-0" role="menu">
          <PaletteMenuItem
            v-for="item in visibleItems"
            :key="item.id"
            :item="item"
            :is-active="activeSecondaryPanel === item.id"
            :is-disabled="isItemDisabled(item)"
            :submenu-items="getSubmenuItems(item)"
            @click="handleItemClick"
            @submenu-click="handleSubmenuClick"
          />
        </v-list>
      </div>

      <!-- Footer (optional) -->
      <div v-if="$slots.footer" class="palette-footer">
        <slot name="footer" />
      </div>
    </v-sheet>
  </v-slide-x-transition>
</template>

<script>
import { mapGetters } from 'vuex';
import { getGroupById, getItemsForGroup } from '@Share/config/menuConfig';
import { $has } from '@/utils';
import PaletteMenuItem from './PaletteMenuItem.vue';

export default {
  name: 'PrimaryPalette',

  components: {
    PaletteMenuItem,
  },

  props: {
    /**
     * ID of the group to display
     */
    groupId: {
      type: String,
      required: true,
    },

    /**
     * Whether the palette is visible
     */
    isOpen: {
      type: Boolean,
      required: true,
    },
  },

  computed: {
    ...mapGetters('GENERAL', {
      isPinned: 'GET_IS_PINNED',
      activeSecondaryPanel: 'GET_ACTIVE_SECONDARY_PANEL',
    }),

    ...mapGetters({
      isModelLoaded: 'GET_PAGE_WITH_TYPE_3D',
      taskBoards: 'TASK_MANAGER/GET_ENTRIES_BY_USER',
      registers: 'REGISTERS/GET_REGISTERS',
      storageLimits: 'BLOB_STORAGE/GET_FACILITY_LIMITS',
    }),

    /**
     * Get the current group config
     */
    group() {
      return getGroupById(this.groupId);
    },

    /**
     * Get the group title for display
     */
    groupTitle() {
      return this.group?.title || '';
    },

    /**
     * Get menu items for current group
     */
    menuItems() {
      return getItemsForGroup(this.groupId);
    },

    /**
     * Filter items by permission and visibility conditions
     */
    visibleItems() {
      return this.menuItems.filter((item) => {
        // Permission check - show item but may be disabled
        return true;
      });
    },

    /**
     * Click-outside directive config
     * Includes icon strip in exclusion list
     */
    clickOutsideConfig() {
      return {
        handler: this.onClickOutside,
        include: () => {
          // Include icon strip and secondary palette
          const elements = [
            document.querySelector('#icon-strip-menu'),
            document.querySelector('.secondary-palette'),
          ].filter(Boolean);
          return elements;
        },
      };
    },
  },

  methods: {
    $has,

    /**
     * Check if a model is loaded (for items requiring model)
     */
    checkModelLoaded() {
      return this.$store.getters['GET_PAGE_WITH_TYPE_3D'] &&
             !this.$store.state.viewerState?.isLoading;
    },

    /**
     * Check if item should be disabled
     */
    isItemDisabled(item) {
      // Check requiresModel
      if (item.requiresModel && !this.checkModelLoaded()) {
        return true;
      }

      // Check scan download special case
      if (item.id === 'scan-download') {
        if (!this.storageLimits?.canDownload) {
          return true;
        }
      }

      return false;
    },

    /**
     * Get submenu items for dynamic items (Task Boards, Registers)
     */
    getSubmenuItems(item) {
      if (item.type !== 'submenu') {
        return null;
      }

      if (item.id === 'task-boards') {
        return this.taskBoards || [];
      }

      if (item.id === 'registers') {
        return this.registers || [];
      }

      return [];
    },

    /**
     * Handle menu item click
     */
    handleItemClick({ itemId, item }) {
      if (item.type === 'panel') {
        // Open secondary palette with panel
        this.$store.dispatch('GENERAL/OPEN_SECONDARY_PANEL', itemId);
      } else if (item.type === 'route') {
        // Navigate to route
        const facilityId = this.$route.params.id;
        const route = `/${facilityId}${item.routePath}`;
        this.$router.push(route);

        // Close palette after navigation
        this.$store.dispatch('GENERAL/CLOSE_ALL_PALETTES');
      }

      this.$emit('item-click', { itemId, type: item.type });
    },

    /**
     * Handle submenu item click (already navigates in PaletteMenuItem)
     */
    handleSubmenuClick(payload) {
      // Close palette after navigation
      this.$store.dispatch('GENERAL/CLOSE_ALL_PALETTES');
      this.$emit('submenu-click', payload);
    },

    /**
     * Handle pin toggle
     */
    handlePinToggle() {
      this.$store.dispatch('GENERAL/TOGGLE_PIN');
      this.$emit('pin-toggle', { isPinned: !this.isPinned });
    },

    /**
     * Handle click outside palette
     */
    onClickOutside() {
      // Don't close if pinned
      if (this.isPinned) {
        return;
      }

      this.$emit('close');
      this.$store.dispatch('GENERAL/CLOSE_PRIMARY_PALETTE');
    },
  },
};
</script>

<style lang="scss" scoped>
.primary-palette {
  position: fixed;
  top: var(--ds-nav-top-bar-height);
  left: var(--ds-nav-icon-strip-width);
  width: var(--ds-nav-primary-palette-width);
  height: calc(100vh - var(--ds-nav-top-bar-height));
  display: flex;
  flex-direction: column;
  z-index: 7;
  border-radius: 0 var(--ds-radius-md) var(--ds-radius-md) 0;
  overflow: hidden;

  // Slide animation
  &.v-enter-active,
  &.v-leave-active {
    transition: transform var(--ds-transition-slow);
    will-change: transform;
  }

  &.v-enter,
  &.v-leave-to {
    transform: translateX(-100%);
  }
}

.palette-header {
  display: flex;
  align-items: center;
  padding: var(--ds-space-md) var(--ds-space-md) var(--ds-space-sm);
  border-bottom: 1px solid var(--ds-border-subtle);

  .palette-title {
    font-size: var(--ds-font-body);
    font-weight: 600;
    color: rgba(255, 255, 255, 0.87);
    letter-spacing: 0.5px;
    margin: 0;
  }
}

.palette-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: var(--ds-space-sm) 0;

  // Custom scrollbar
  &::-webkit-scrollbar {
    width: var(--ds-space-xs);
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
}

.palette-footer {
  padding: var(--ds-space-sm) var(--ds-space-md);
  border-top: 1px solid var(--ds-border-subtle);
}

// Mobile responsive
@media (max-width: 768px) {
  .primary-palette {
    left: 0;
    width: 100vw;
    height: 100vh;
    top: 0;
    border-radius: 0;
    z-index: 100;
  }
}
</style>
